import React from 'react';
import { View, Text, Pressable, ScrollView } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useConsole, NAVDEF } from '../context/voiceConsoleContext';
import Icon from '../components/icon';

// Sidebar — pixel-faithful to the HTML <aside>. Two widths: full (236px, labels +
// role tag + "View as") and a collapsed icon-only rail (64px: logo + nav icons).
// The header chevron toggles it; the topbar hamburger reopens from the rail.
export default function VoiceSidebar({ liveCount, openFollowups }: { liveCount: number; openFollowups: number }) {
  const { theme } = useTheme();
  const c = theme.colors;
  const { role, platformRole, screen, tenant, go, setupComplete, sidebarCollapsed, toggleSidebar } = useConsole();

  const collapsed = sidebarCollapsed;
  // Hide the "Start here" (Set up SK Voice) group once onboarding is complete.
  const nav = setupComplete ? NAVDEF[role].filter((g) => g.group !== 'Start here') : NAVDEF[role];
  const roleTag =
    role === 'softknack' ? 'platform admin · setup'
    : `${platformRole ?? 'member'} · ${tenant.mark.toLowerCase()}`;

  const badgeFor = (id: string) =>
    id === 'intents' ? (liveCount ? String(liveCount) : '')
    : id === 'followups' ? (openFollowups ? String(openFollowups) : '')
    : '';

  return (
    <View style={{ width: collapsed ? 64 : 236, flexShrink: 0, borderRightWidth: 1, borderRightColor: c.line, backgroundColor: c.paper2, height: '100%' }}>
      {/* Header: logo + (full only) name/role, + collapse chevron */}
      <View style={{ height: 56, flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: collapsed ? 0 : 18, justifyContent: collapsed ? 'center' : 'flex-start', borderBottomWidth: 1, borderBottomColor: c.line }}>
        <View style={{ width: 26, height: 26, borderRadius: 6, backgroundColor: c.ink, alignItems: 'center', justifyContent: 'center' }}>
          <Text style={{ color: c.paper, fontWeight: '700', fontSize: 14, letterSpacing: -0.3, fontFamily: theme.font.sans }}>SK</Text>
        </View>
        {!collapsed && (
          <>
            <View style={{ flex: 1 }}>
              <Text style={{ fontWeight: '600', fontSize: 14, letterSpacing: -0.1, color: c.ink, fontFamily: theme.font.sans }}>SK Voice</Text>
              <Text style={{ fontSize: 10, color: c.ink3, fontFamily: theme.font.mono }}>{roleTag}</Text>
            </View>
            <Pressable onPress={toggleSidebar} accessibilityLabel="Collapse sidebar"
              style={{ width: 28, height: 28, borderRadius: 7, borderWidth: 1, borderColor: c.line, backgroundColor: c.card, alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="chev-l" size={15} color={c.ink2} />
            </Pressable>
          </>
        )}
      </View>

      {/* Nav — labels + badges hidden when collapsed (icons only). */}
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: collapsed ? 8 : 10, paddingTop: 12 }}>
        {nav.map((grp) => (
          <View key={grp.group} style={{ marginBottom: 14 }}>
            {!collapsed && (
              <Text style={{ fontSize: 10, fontWeight: '600', letterSpacing: 0.8, textTransform: 'uppercase', color: c.ink4, paddingHorizontal: 10, paddingBottom: 6, fontFamily: theme.font.sans }}>
                {grp.group}
              </Text>
            )}
            {grp.items.map(([id, label, icon]) => {
              const active = screen === id;
              const badge = badgeFor(id);
              return (
                <Pressable key={id} onPress={() => go(id)} accessibilityLabel={label}
                  style={{ flexDirection: 'row', alignItems: 'center', gap: 10, width: '100%', height: 34, paddingHorizontal: collapsed ? 0 : 10, justifyContent: collapsed ? 'center' : 'flex-start', borderRadius: 7, backgroundColor: active ? c.ink : 'transparent', marginBottom: 2 }}>
                  <View>
                    <Icon name={icon} size={16} color={active ? c.paper : c.ink2} />
                    {/* collapsed: badge count shrinks to a dot on the icon */}
                    {collapsed && !!badge && (
                      <View style={{ position: 'absolute', top: -3, right: -4, width: 7, height: 7, borderRadius: 999, backgroundColor: active ? c.paper : c.ink }} />
                    )}
                  </View>
                  {!collapsed && (
                    <>
                      <Text style={{ flex: 1, fontSize: 13, fontWeight: active ? '600' : '500', color: active ? c.paper : c.ink2, fontFamily: theme.font.sans }}>{label}</Text>
                      {!!badge && (
                        <View style={{ backgroundColor: active ? 'rgba(255,255,255,0.2)' : c.paper3, borderWidth: active ? 0 : 1, borderColor: c.line, borderRadius: 20, paddingHorizontal: 7, paddingVertical: 1 }}>
                          <Text style={{ fontFamily: theme.font.mono, fontSize: 10, color: active ? c.paper : c.ink3 }}>{badge}</Text>
                        </View>
                      )}
                    </>
                  )}
                </Pressable>
              );
            })}
          </View>
        ))}
      </ScrollView>
    </View>
  );
}
